import React, { useState } from "react";
import { BigFormModal } from "@/components/ui/big-form-modal";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, Copy, Clock, FileText, Zap, BarChart3, Eye, Code, Layers } from "lucide-react";
import { toast } from "sonner";
import { HtmlSanitizeMetrics } from "@/services/htmlSanitizeService";

interface HtmlSanitizeModalProps {
  isOpen: boolean;
  onClose: () => void;
  sanitizedHtml: string;
  rawHtmlContent: string;
  programName: string;
  dataFetchUrl: string;
  isFetching: boolean;
  fetchError: string | null;
  metrics?: HtmlSanitizeMetrics;
}

export const HtmlSanitizeModal: React.FC<HtmlSanitizeModalProps> = ({
  isOpen,
  onClose,
  sanitizedHtml,
  rawHtmlContent,
  programName,
  dataFetchUrl,
  isFetching,
  fetchError,
  metrics
}) => {
  const [isTechnicalSummaryOpen, setIsTechnicalSummaryOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'preview' | 'output' | 'fetched'>('preview');

  const handleCopyOutput = async () => {
    try {
      await navigator.clipboard.writeText(sanitizedHtml);
      toast.success("HTML output copied to clipboard");
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      toast.error("Failed to copy to clipboard");
    }
  };

  const handleCopyFetched = async () => {
    try {
      await navigator.clipboard.writeText(rawHtmlContent);
      toast.success("HTML fetched copied to clipboard");
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      toast.error("Failed to copy to clipboard");
    }
  };

  const formatTime = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  return (
    <BigFormModal 
      isOpen={isOpen} 
      onClose={onClose}
      title={`HTML Sanitizer - ${programName}`}
      hideActions={true}
    >
      <div className="space-y-6">
        {/* URL Info */}
        <div className="bg-gray-50 p-3 rounded-lg">
          <p className="text-sm text-gray-600">Processing URL:</p>
          <p className="font-mono text-sm break-all">{dataFetchUrl}</p>
          {dataFetchUrl.includes(' ') || dataFetchUrl.includes('\n') || dataFetchUrl.includes(',') ? (
            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
              <p className="text-xs text-blue-700">
                📋 <strong>Multiple URLs detected.</strong> The HTML Sanitizer processes the first valid URL only.
                For comprehensive processing of all URLs, use the "Fetch session dates" action instead.
              </p>
            </div>
          ) : null}
        </div>

        {/* Loading State */}
        {isFetching && (
          <div className="flex items-center justify-center py-8">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="text-gray-600">Fetching and sanitizing HTML content...</span>
            </div>
          </div>
        )}

        {/* Error State */}
        {fetchError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-red-800 mb-2">
              <FileText className="w-5 h-5" />
              <span className="font-semibold">Sanitization Failed</span>
            </div>
            <p className="text-red-700">{fetchError}</p>
            
            {/* Special guidance for SPA/JavaScript issues */}
            {fetchError.toLowerCase().includes('single page application') || 
             fetchError.toLowerCase().includes('javascript') ||
             fetchError.toLowerCase().includes('angular') ||
             fetchError.toLowerCase().includes('react') ||
             fetchError.toLowerCase().includes('minimal content') ? (
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm font-semibold text-blue-800 mb-2">🤖 Single Page Application Detected</p>
                <p className="text-sm text-blue-700 mb-2">
                  This URL appears to load content dynamically with JavaScript. Try these solutions:
                </p>
                <ul className="text-sm text-blue-700 space-y-1 ml-4 list-disc">
                  <li><strong>Check for API endpoints:</strong> Look for `/api/events/` or similar data endpoints</li>
                  <li><strong>Contact the provider:</strong> Ask for a server-rendered or static version of the page</li>
                  <li><strong>Try alternate URLs:</strong> Look for mobile versions or direct data feeds</li>
                  <li><strong>Manual extraction:</strong> Copy session data manually from the website</li>
                </ul>
              </div>
            ) : (
              <p className="text-sm text-red-600 mt-2">
                Please check the URL and try again, or process the content manually.
              </p>
            )}
          </div>
        )}

        {/* Success State - Sanitized HTML Content */}
        {!isFetching && !fetchError && sanitizedHtml && (
          <>
            {/* Content Preview with View Toggle */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Sanitized HTML Content</h3>
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('preview')}
                    className={`flex items-center gap-2 px-3 py-1 rounded text-sm transition-colors ${
                      viewMode === 'preview' 
                        ? 'bg-white shadow-sm text-blue-600' 
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    <Eye className="w-4 h-4" />
                    Preview
                  </button>
                  <button
                    onClick={() => setViewMode('output')}
                    className={`flex items-center gap-2 px-3 py-1 rounded text-sm transition-colors ${
                      viewMode === 'output' 
                        ? 'bg-white shadow-sm text-blue-600' 
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    <Code className="w-4 h-4" />
                    HTML Output
                  </button>
                  <button
                    onClick={() => setViewMode('fetched')}
                    className={`flex items-center gap-2 px-3 py-1 rounded text-sm transition-colors ${
                      viewMode === 'fetched' 
                        ? 'bg-white shadow-sm text-blue-600' 
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    HTML Fetched
                  </button>
                </div>
              </div>

              <div className="border rounded-lg">
                <div className="bg-gray-50 px-4 py-2 border-b">
                  <p className="text-sm text-gray-600">
                    {viewMode === 'preview' && 'Preview (HTML rendered)'}
                    {viewMode === 'output' && 'Sanitized HTML output'}
                    {viewMode === 'fetched' && 'Raw HTML fetched from URL'}
                  </p>
                </div>
                
                {viewMode === 'preview' ? (
                  <div 
                    className="p-4 max-h-96 overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                  />
                ) : (
                  <div className="max-h-96 overflow-y-auto">
                    <pre className="p-4 text-sm text-gray-800 font-mono whitespace-pre-wrap break-words">
                      <code>{viewMode === 'output' ? sanitizedHtml : rawHtmlContent}</code>
                    </pre>
                  </div>
                )}
              </div>
            </div>

            {/* Technical Summary - Collapsible */}
            {metrics && (
              <Collapsible open={isTechnicalSummaryOpen} onOpenChange={setIsTechnicalSummaryOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" className="w-full justify-between">
                    <span className="flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" />
                      Technical Summary
                    </span>
                    {isTechnicalSummaryOpen ? 
                      <ChevronDown className="w-4 h-4" /> : 
                      <ChevronRight className="w-4 h-4" />
                    }
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-4">
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    
                    {/* Pattern Application Metrics (if available) */}
                    {metrics.patternApplicationMetrics && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                        <div className="flex items-center gap-2 mb-3">
                          <Layers className="w-5 h-5 text-blue-600" />
                          <span className="font-semibold text-blue-800">Pattern Application Results</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="font-medium text-blue-700">Patterns Applied:</span>
                            <span className="ml-2 text-blue-600">
                              {metrics.patternApplicationMetrics.patternsFound}
                            </span>
                          </div>
                          <div>
                            <span className="font-medium text-blue-700">Pattern Reduction:</span>
                            <span className="ml-2 font-semibold text-blue-600">
                              {metrics.patternApplicationMetrics.patternReductionPercent.toFixed(1)}%
                            </span>
                          </div>
                          <div className="md:col-span-2">
                            <span className="font-medium text-blue-700">Content after patterns:</span>
                            <span className="ml-2 text-blue-600">
                              {formatNumber(metrics.patternApplicationMetrics.htmlAfterPatternsLength)} characters
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium">URL Processed:</span>
                      </div>
                      <p className="text-sm font-mono break-all">{dataFetchUrl}</p>

                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-orange-600" />
                        <span className="text-sm font-medium">Time Elapsed:</span>
                      </div>
                      <p className="text-sm">{formatTime(metrics.timeElapsedMs)}</p>

                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-600" />
                        <span className="text-sm font-medium">Original Characters:</span>
                      </div>
                      <p className="text-sm">{formatNumber(metrics.originalCharCount)}</p>

                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-purple-600" />
                        <span className="text-sm font-medium">Original Tokens (est.):</span>
                      </div>
                      <p className="text-sm">{formatNumber(metrics.originalTokenCount)}</p>

                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium">Sanitized Characters:</span>
                      </div>
                      <p className="text-sm">{formatNumber(metrics.sanitizedCharCount)}</p>

                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium">Sanitized Tokens (est.):</span>
                      </div>
                      <p className="text-sm">{formatNumber(metrics.sanitizedTokenCount)}</p>
                    </div>

                    {/* Reduction Summary */}
                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Total Content Reduction:</span>
                        <span className="text-sm font-semibold text-green-600">
                          {metrics.contentReductionPercent.toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Token Reduction (est.):</span>
                        <span className="text-sm font-semibold text-blue-600">
                          {((1 - metrics.sanitizedTokenCount / metrics.originalTokenCount) * 100).toFixed(1)}%
                        </span>
                      </div>
                      {metrics.patternApplicationMetrics && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <p className="text-xs text-gray-600">
                            🎯 Patterns contributed {metrics.patternApplicationMetrics.patternReductionPercent.toFixed(1)}% reduction, 
                            sanitization added {(metrics.contentReductionPercent - metrics.patternApplicationMetrics.patternReductionPercent).toFixed(1)}% additional reduction
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4 border-t">
          {!isFetching && !fetchError && sanitizedHtml && (
            <>
              <Button onClick={handleCopyOutput} className="flex items-center gap-2">
                <Copy className="w-4 h-4" />
                Copy HTML output
              </Button>
              <Button onClick={handleCopyFetched} variant="outline" className="flex items-center gap-2">
                <Copy className="w-4 h-4" />
                Copy HTML fetched
              </Button>
            </>
          )}
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </BigFormModal>
  );
}; 